// Replaces src/renderer/src/extensions/null_activator/index.ts in the web build.
//
// A real, copy-based deployment method. The desktop activators deploy by
// hardlink/symlink/move, none of which work against a File System Access
// game mount (no links; move would empty the OPFS staging). This copies each
// staged file (read from OPFS) into the game folder (written through the
// read-write FSA handle, see fsa-core mountWriteFile), which is what actually
// makes the game load the mod.
//
// It extends the renderer's LinkingActivator base (resolved via the `@`
// webpack alias), reusing all of its prepare/activate/finalize diffing and
// deployment-manifest handling; only the link primitives are overridden to
// copy/remove instead of hardlink.
//
// The id is kept as "null-deployment" so games already managed with the
// previous null activator stay pointed at a valid (now functional) method.
const path = require("path");

const LinkingActivator =
    require("@/extensions/mod_management/LinkingDeployment").default;
const fs = require("@/util/fs");

class CopyDeployment extends LinkingActivator {
    constructor(api) {
        super(
            "null-deployment",
            "Copy Deployment (web)",
            "Copies mod files from staging into the game folder. Used in the " +
                "web build, where hardlink/symlink deployment isn't available.",
            true, // fallbackPurgeSafe: copies are disposable, manifest suffices
            api,
        );
        this.priority = 1; // prefer this over hardlink/symlink/move in web
    }

    isSupported() {
        return undefined; // always supported in the web build
    }

    canRestore() {
        return false; // a copy can't be restored after the source is deleted
    }

    async linkFile(linkPath, sourcePath, dirTags) {
        await this.ensureDir(path.dirname(linkPath), dirTags);
        try {
            await fs.copyAsync(sourcePath, linkPath);
        } catch (err) {
            if (err && err.code === "EEXIST") {
                await fs.removeAsync(linkPath);
                await fs.copyAsync(sourcePath, linkPath);
            } else {
                throw err;
            }
        }
    }

    async unlinkFile(linkPath) {
        await fs.removeAsync(linkPath);
    }

    // after deploying, activate any deployed Bethesda plugins in plugins.txt
    // (deploying the .esp/.esm into the game folder isn't enough — the game
    // only loads plugins listed in plugins.txt under AppData)
    async finalize(gameId, dataPath, installationPath, progressCB) {
        const result = await super.finalize(
            gameId,
            dataPath,
            installationPath,
            progressCB,
        );
        try {
            const pluginTxt = require("./plugin-txt.cjs");
            await pluginTxt.syncAfterDeploy(
                gameId,
                (result || []).map((entry) => entry.relPath),
            );
        } catch (err) {
            console.warn("[vortex-web] plugins.txt sync failed", err);
        }
        return result;
    }

    // copies have no shared inode or marker; treat any existing file at the
    // deployed path as our deployment (good enough for the web build, avoids
    // spurious "external change" churn)
    isLink(linkPath) {
        return fs.statAsync(linkPath).then(
            () => true,
            () => false,
        );
    }

    // full "Purge Mods": per-mod removal goes through finalize()'s diff
    // (unlinkFile per file) and doesn't need this. For a full purge we read
    // the deployment manifest(s) the core wrote into the game folder and
    // delete each listed file.
    async purgeLinks(installPath, dataPath) {
        let names;
        try {
            names = await fs.readdirAsync(dataPath);
        } catch (err) {
            return;
        }
        const manifests = names.filter(
            (n) => n.startsWith("vortex.deployment") && n.endsWith(".json"),
        );
        for (const manifestName of manifests) {
            let parsed;
            try {
                const raw = await fs.readFileAsync(
                    path.join(dataPath, manifestName),
                    { encoding: "utf8" },
                );
                parsed = JSON.parse(raw);
            } catch (err) {
                continue;
            }
            const files = parsed?.files ?? [];
            for (const entry of files) {
                if (entry?.relPath === undefined) continue;
                await fs
                    .removeAsync(path.join(dataPath, entry.relPath))
                    .catch(() => undefined);
            }
        }
    }
}

function init(context) {
    context.registerDeploymentMethod(new CopyDeployment(context.api));
    return true;
}

module.exports = init;
module.exports.default = init;
